import React, { useMemo, useState } from "react";
import { AlertTriangle, Check, X, Zap, PanelRightClose, PanelRightOpen } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const sevOrder = { critical: 0, high: 1, medium: 2, low: 3 };

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const SeverityChip = ({ sev }) => {
  const map = {
    critical: "bg-sev-critical",
    high: "bg-sev-high",
    medium: "bg-sev-medium",
    low: "bg-sev-low",
  };
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${map[sev] || "bg-sev-low"}`}>
      {titleCase(sev)}
    </span>
  );
};

const relTime = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

export const RightAlertRail = ({ alerts, onUpdate, expanded = false, onToggle }) => {
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    const list = alerts.filter((a) => a.status !== "resolved");
    const f = filter === "all" ? list : list.filter((a) => a.severity === filter);
    return [...f].sort((a, b) =>
      sevOrder[a.severity] - sevOrder[b.severity] ||
      new Date(b.ts) - new Date(a.ts)
    );
  }, [alerts, filter]);

  const handleAck = async (id) => {
    onUpdate(id, { status: "ack" });
    try {
      await api.ackEvent(id);
      toast.success("Alert acknowledged");
    } catch {
      toast.error("Failed to acknowledge");
    }
  };
  const handleDismiss = async (id) => {
    onUpdate(id, { status: "resolved" });
    try {
      await api.dismissEvent(id);
      toast("Alert dismissed");
    } catch {
      toast.error("Failed to dismiss");
    }
  };

  // Collapsed strip view
  if (!expanded) {
    return (
      <aside
        className="hidden lg:flex flex-col w-10 border-l border-border bg-[hsl(var(--surface))] shrink-0"
        data-testid="alert-rail"
        data-expanded="false"
      >
        <button
          onClick={onToggle}
          aria-label="Expand alerts panel"
          title="Expand alerts panel"
          data-testid="alert-rail-toggle-btn"
          className="h-12 border-b border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))]"
        >
          <PanelRightOpen className="w-4 h-4" strokeWidth={1.75} />
        </button>
        <div className="flex-1 flex flex-col items-center pt-3 gap-2">
          <AlertTriangle className="w-4 h-4 sev-critical" strokeWidth={2} />
          <span
            className="font-mono text-[10px] font-bold text-foreground"
            data-testid="alert-count"
          >
            {filtered.length}
          </span>
          <span
            className="[writing-mode:vertical-rl] rotate-180 mt-1 font-display font-black text-[10px] text-muted-foreground"
            aria-hidden="true"
          >
            Live Alerts
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="hidden lg:flex flex-col w-72 xl:w-80 border-l border-border bg-[hsl(var(--surface))] shrink-0"
      data-testid="alert-rail"
      data-expanded="true"
    >
      <div className="h-12 border-b border-border flex items-center justify-between px-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 sev-critical shrink-0" strokeWidth={2} />
          <span className="font-display font-black text-sm">Live Alerts</span>
          <span className="font-mono text-xs text-muted-foreground" data-testid="alert-count">
            {filtered.length}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              className="h-7 w-24 px-2 text-[11px] font-medium bg-transparent border-border rounded-none"
              data-testid="alert-filter-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {["all", "critical", "high", "medium"].map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  className="text-[11px]"
                  data-testid={`alert-filter-${s}`}
                >
                  {titleCase(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={onToggle}
            aria-label="Collapse alerts panel"
            title="Collapse alerts panel"
            data-testid="alert-rail-toggle-btn"
            className="p-1 text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))]"
          >
            <PanelRightClose className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="alert-list">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-xs text-muted-foreground">
            No active alerts in this filter.
          </div>
        )}
        {filtered.map((a) => (
          <div
            key={a.id}
            data-testid={`alert-item-${a.id}`}
            className="group border-b border-border p-3 hover:bg-[hsl(var(--surface-elevated))] transition-colors"
          >
            <div className="flex items-start gap-2">
              <SeverityChip sev={a.severity} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm leading-tight">{a.title}</div>
                <div className="mt-1 text-[11px] text-muted-foreground font-mono flex flex-wrap items-center gap-x-2">
                  <span>{a.camera_id ?? "—"}</span>
                  <span>·</span>
                  <span>{a.zone_name}</span>
                  <span>·</span>
                  <span>{relTime(a.ts)}</span>
                </div>
                <div className="mt-1.5 text-[11px] text-muted-foreground leading-snug line-clamp-2">
                  {a.description}
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <div className="flex items-center gap-1 text-[10px] font-mono tracking-wide text-muted-foreground">
                    <Zap className="w-3 h-3 text-[hsl(var(--primary))]" strokeWidth={2} />
                    Risk {Math.round((a.risk_score ?? 0) * 100)}
                  </div>
                  <div className="flex-1" />
                  {a.status !== "ack" && (
                    <button
                      data-testid={`alert-ack-${a.id}`}
                      onClick={() => handleAck(a.id)}
                      className="btn-tac-primary"
                    >
                      <Check className="w-3 h-3" /> Ack
                    </button>
                  )}
                  <button
                    data-testid={`alert-dismiss-${a.id}`}
                    onClick={() => handleDismiss(a.id)}
                    className="btn-tac"
                    aria-label="Dismiss alert"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>

                {a.status === "ack" && (
                  <div className="mt-1.5 text-[10px] uppercase tracking-widest font-bold sev-low">
                    Acknowledged
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default RightAlertRail;
